// src/pages/Profile.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  FaUser, FaEdit, FaCamera, FaGraduationCap,
  FaHeart, FaStar, FaCalendar, FaUsers, FaCheckCircle,
  FaEye, FaShare, FaSpinner, FaSave, FaTimes, FaArrowLeft,
  FaTrash, FaNotesMedical, FaPhone,
} from 'react-icons/fa';
import { useNavigate } from "react-router-dom";
import { message } from 'antd';
import {
  Hindu, Muslim, MuslimShia, MuslimSunni,
  Christian, Sikh, JainDigambar, JainShwetambar,
  Parsi, Buddhist, Jewish, InterReligion, NoReligiousBelief
} from 'indian-religion-caste';
import {
  User_StateGet,
  getDistrictsByState,
  getCitiesByDistrict,
  getReligionOptions,
} from "../../Api/CoreApi";

import Api from "../../Api/Api";

const Profile = () => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [profileData, setProfileData] = useState({
    languages: [], hobbies: [], interests: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editableData, setEditableData] = useState({});
  const [imageUploading, setImageUploading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [filteredCastes, setFilteredCastes] = useState([]);
  const [religionOptions, setReligionOptions] = useState([]);
  const userId = localStorage.getItem("user_id")?.replace(/\/$/, '') || '';
  const [lastLogin, setLastLogin] = useState('N/A');
  const cmToFeetInches = (cm) => {
    if (!cm) return '';
    // "140 cm" ya "140" dono handle karo
    const numCm = parseInt(String(cm).replace(/[^0-9]/g, ''));
    if (isNaN(numCm)) return cm; // agar already feet format hai to as-is return karo

    const totalInches = numCm / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return `${feet}'${inches}" (${numCm} cm)`;
  };

  // Feet'Inches" to CM convert karo (save ke time)
  const feetInchesToCm = (feetStr) => {
    if (!feetStr) return '';
    // "4'7" (140 cm)" se cm extract karo
    const cmMatch = String(feetStr).match(/\((\d+)\s*cm\)/);
    if (cmMatch) return cmMatch[1]; // "140" return karega

    // "4'7"" format handle karo
    const feetMatch = String(feetStr).match(/(\d+)'(\d+)"/);
    if (feetMatch) {
      const feet = parseInt(feetMatch[1]);
      const inches = parseInt(feetMatch[2]);
      return String(Math.round((feet * 30.48) + (inches * 2.54)));
    }

    // Already CM hai to return karo
    const numOnly = parseInt(String(feetStr).replace(/[^0-9]/g, ''));
    return isNaN(numOnly) ? feetStr : String(numOnly);
  };

  // Height options generate karo (4'0" to 7'0")
  const generateHeightOptions = () => {
    const heights = [];
    for (let feet = 4; feet <= 7; feet++) {
      const maxInches = feet === 7 ? 0 : 11;
      for (let inches = 0; inches <= maxInches; inches++) {
        const totalCm = Math.round((feet * 30.48) + (inches * 2.54));
        const label = `${feet}'${inches}" (${totalCm} cm)`;
        heights.push(label);
      }
    }
    return heights;
  };
  const isUserOnline = (data) => {
    // Case 1: Backend ne true diya
    if (data.online === true || data.is_online === true) return true;

    // Case 2: last_seen se calculate (agar future me backend add kare)
    if (data.last_seen) {
      const diffMinutes = (new Date() - new Date(data.last_seen)) / (1000 * 60);
      return diffMinutes <= 15;
    }

    // Case 3: Ye apna profile hai = user online hai
    // userId match karo
    const loggedInId = localStorage.getItem("user_id")?.replace(/\/$/, '');
    const profileUserId = data.id || data.user_id || data.bid || '';
    if (loggedInId && profileUserId && loggedInId === profileUserId) {
      return true; // ✅ Apna profile = Online
    }

    return false;
  };

  const extractCasteNames = (casteArray) => {
    if (!Array.isArray(casteArray)) return [];
    return casteArray.map(item => {
      if (typeof item === 'string') return item;
      if (typeof item === 'object' && item !== null) {
        return item.name || item.caste || item.label ||
          item.value || Object.values(item)[0] || '';
      }
      return String(item);
    }).filter(item => item && typeof item === 'string' && item.trim() !== '');
  };
  const fetchStates = useCallback(async () => {
    try {
      const res = await User_StateGet();
      if (Array.isArray(res)) setStates(res);
      else if (res?.data) setStates(res.data);
      else setStates([]);
    } catch (error) {
      setStates([]);
    }
  }, []);

  const fetchDistricts = useCallback(async (stateName) => {
    if (!stateName) { setDistricts([]); return; }
    try {
      const response = await getDistrictsByState(stateName);
      const districtList = response?.districts || (Array.isArray(response) ? response : []);
      setDistricts(districtList);
    } catch (error) {
      setDistricts([]);
    }
  }, []);
  const fetchReligions = useCallback(async () => {
    try {
      const res = await getReligionOptions();
      const religions = res?.religions || [];
      if (religions.length > 0) {
        setReligionOptions(religions);
      } else {
        // ✅ Fallback
        setReligionOptions([
          { id: 1, value: 'Hindu', label: 'Hindu', emoji: '🕉️' },
          { id: 2, value: 'Muslim', label: 'Muslim', emoji: '🕌' },
          { id: 3, value: 'Christian', label: 'Christian', emoji: '⛪' },
          { id: 4, value: 'Sikh', label: 'Sikh', emoji: '🪯' },
          { id: 5, value: 'Jain', label: 'Jain', emoji: '✋' },
          { id: 6, value: 'Buddhist', label: 'Buddhist', emoji: '☸️' },
          { id: 7, value: 'Parsi', label: 'Parsi', emoji: '🔥' },
          { id: 8, value: 'Jewish', label: 'Jewish', emoji: '✡️' },
          { id: 9, value: 'InterReligion', label: 'Inter Religion', emoji: '🤝' },
          { id: 10, value: 'NoReligiousBelief', label: 'No Religious Belief', emoji: '🌿' },
        ]);
      }
    } catch (error) {
      // ✅ Error pe bhi fallback
      setReligionOptions([
        { id: 1, value: 'Hindu', label: 'Hindu', emoji: '🕉️' },
        { id: 2, value: 'Muslim', label: 'Muslim', emoji: '🕌' },
        { id: 3, value: 'Christian', label: 'Christian', emoji: '⛪' },
        { id: 4, value: 'Sikh', label: 'Sikh', emoji: '🪯' },
        { id: 5, value: 'Jain', label: 'Jain', emoji: '✋' },
        { id: 6, value: 'Buddhist', label: 'Buddhist', emoji: '☸️' },
        { id: 7, value: 'Parsi', label: 'Parsi', emoji: '🔥' },
        { id: 8, value: 'Jewish', label: 'Jewish', emoji: '✡️' },
        { id: 9, value: 'InterReligion', label: 'Inter Religion', emoji: '🤝' },
        { id: 10, value: 'NoReligiousBelief', label: 'No Religious Belief', emoji: '🌿' },
      ]);
    }
  }, []);
  const validateSiblingCount = (key, value, currentData) => {
    const num = parseInt(value) || 0;

    if (key === 'brother_marrige') {
      const totalBrothers = parseInt(currentData.brother) || 0;
      if (num > totalBrothers) {
        message.warning(`⚠️ Married Brothers cannot exceed Total Brothers (${totalBrothers})`);
        return totalBrothers; // Max value return karo
      }
    }

    if (key === 'sister_marrige') {
      const totalSisters = parseInt(currentData.sister) || 0;
      if (num > totalSisters) {
        message.warning(`⚠️ Married Sisters cannot exceed Total Sisters (${totalSisters})`);
        return totalSisters;
      }
    }

    // ✅ Negative value nahi honi chahiye
    if (num < 0) return 0;

    return num;
  };

  const fetchCities = useCallback(async (districtName) => {
    if (!districtName) { setCities([]); return; }
    try {
      const response = await getCitiesByDistrict(districtName);
      const cityList = response?.cities || (Array.isArray(response) ? response : []);
      setCities(cityList);
    } catch (error) {
      setCities([]);
    }
  }, []);

  const fetchProfile = useCallback(async () => {
    if (!userId) {
      message.error("Please login again");
      navigate("/User_Login");
      return;
    }
    try {
      setLoading(true);
      const res = await Api.get(`/api/user/profile/${userId}/`);
      if (res.data) {
        const data = res.data;

        const profileObj = {
          ...data,
          profileViews: data.profileViews || 0,
          shortlistedBy: data.shortlistedBy || 0,
          interestsReceived: data.interestsReceived || 0,
          
          last_login: data.last_login || "N/A",
          profile_id: data.profile_id || data.bid || '',
          languages: Array.isArray(data.languages) ? data.languages : [],
          hobbies: Array.isArray(data.hobbies) ? data.hobbies : [],
          interests: Array.isArray(data.interests) ? data.interests : [],
          name: data.name || data.firstname || data.first_name || '',
          gender: data.gender || '',
          age: data.age || '',
          dob: data.dob || '',
          height: data.height || '',
          religion: data.religion || '',
          caste: data.caste || '',
          subcaste: data.subcaste || '',
          motherTongue: data.motherTongue || data.mother_tongue || '',
          marrige_status: data.marrige_status || data.maritalstatus || '',
          blood_group: data.blood_group || '',
          smoking: data.smoking || '',
          drinking: data.drinking || '',
          foodLifestyle: data.foodLifestyle || data.Lifestyle || '',
          additional_notes: data.additional_notes || '',
          father_name: data.father_name || '',
          mother_name: data.mother_name || '',
          father_occupation: data.father_occupation || '',
          mother_occupation: data.mother_occupation || '',
          brother: data.brother || 0,
          sister: data.sister || 0,
          brother_marrige: data.brother_marrige || 0,
          sister_marrige: data.sister_marrige || 0,
          family_status: data.family_status || '',
          family_type: data.family_type || '',
          family_values: data.family_values || '',
          university: data.university || '',
          course: data.course || '',
          sub_course: data.sub_course || '',
          job_title: data.job_title || '',
          job_type: data.job_type || '',
          annual_income: data.annual_income || data.income || '',
          Lifestyle: data.Lifestyle || data.foodLifestyle || '',
          created_profile: data.created_profile || '',
          email: data.email || '',
          contact: data.contact || '',
          contact1: data.contact1 || '',
          country: data.country || 'India',
          state: data.state || '',
          district: data.district || '',
          city: data.city || '',
          location: data.location || data.city || data.district || '',
          height_preference: data.height_preference || '',
          age_preference: data.age_preference || '',
          education_preference: data.education_preference || '',
          religion_preference: data.religion_preference || '',
          location_preference: data.location_preference || '',
          partner_smoking: data.partner_smoking || 'Any',
          partner_drinking: data.partner_drinking || 'Any',
          partner_diet: data.partner_diet || 'Any',
          online: isUserOnline(data),
        };
        setProfileData(profileObj);
        setEditableData({ ...profileObj });
        if (profileObj.religion) {
          const casteMap = {
            'Hindu': Hindu,
            'Muslim': MuslimShia,
            'MuslimShia': MuslimShia,
            'MuslimSunni': MuslimSunni,
            'Christian': Christian,
            'Sikh': Sikh,
            'Jain': JainDigambar,
            'JainDigambar': JainDigambar,
            'JainShwetambar': JainShwetambar,
            'Buddhist': Buddhist,
            'Parsi': Parsi,
            'Jewish': Jewish,
            'InterReligion': InterReligion,
            'NoReligiousBelief': NoReligiousBelief,
          };
          const rawCastes = casteMap[profileObj.religion] || [];
          setFilteredCastes(extractCasteNames(rawCastes));
        }
        if (profileObj.state) {
          setSelectedState(profileObj.state);
          await fetchDistricts(profileObj.state);
          if (profileObj.district) {
            setSelectedDistrict(profileObj.district);
            await fetchCities(profileObj.district);
          }
        }
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404) message.error("Profile not found.");
      else if (status === 401) { message.error("Session expired."); navigate("/User_Login"); }
      else message.error("Failed to load profile.");
    } finally {
      setLoading(false);
    }
  }, [userId, navigate, fetchDistricts, fetchCities]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await Api.get(`/user/dashboard/`);
      if (res.data) {
        const d = res.data;
        setProfileData(prev => ({
          ...prev,
          profileViews: d.currentUser?.profileViews || d.profileViews || prev.profileViews || 0,
          shortlistedBy: d.currentUser?.shortlistedBy || d.shortlistedBy || prev.shortlistedBy || 0,
          interestsReceived: d.currentUser?.interestsReceived || d.interestsReceived || prev.interestsReceived || 0,
          online: prev.online,
        }));
      }
    } catch (err) {
      console.warn("Stats fetch failed:", err?.response?.status);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
    fetchStats();
    fetchStates();
    fetchReligions();
  }, [fetchProfile, fetchStats, fetchStates, fetchReligions]);
  useEffect(() => {
    const stored = localStorage.getItem('last_login');
    if (stored) {
      setLastLogin(stored);
    }
  }, []);
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { message.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { message.error('Image must be less than 5MB'); return; }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    message.info('Photo selected! Click Save to upload.');
  };
  const calculateAge = (dob) => {
    if (!dob) return '';
    const birthDate = new Date(dob);
    if (isNaN(birthDate)) return '';
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    // ✅ Birthday abhi nahi aya to age 1 kam karo
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age > 0 ? age : '';
  };
  // Profile.jsx me jaha ye function hai:
const uploadProfileImage = async () => {
    if (!imageFile) return false;
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append("pic", imageFile);
      const cleanUserId = userId.replace(/\/$/, '');
      const response = await Api.put(
        `/api/user/profile/${cleanUserId}/`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (response?.data) {
        const newImg = response.data?.user?.image || response.data?.image || response.data?.pic || null;
        if (newImg) setProfileData(prev => ({ ...prev, image: newImg }));
        message.success("✅ Profile photo updated!");
        setImageFile(null);
        setImagePreview(null);
        const el = document.getElementById('profileUpload');
        if (el) el.value = '';

        // ✅ ADD THIS LINE: Event dispatch karo
        window.dispatchEvent(new Event('profileUpdated'));

        return true;
      }
      return false;
    } catch (error) {
      message.error("Failed to upload photo.");
      return false;
    } finally {
      setImageUploading(false);
    }
};

  const handleStateChange = async (value) => {
    setSelectedState(value);
    setDistricts([]);
    setCities([]);
    setEditableData(prev => ({ ...prev, state: value, district: '', city: '' }));
    if (value) await fetchDistricts(value);
  };

  const handleDistrictChange = async (value) => {
    setSelectedDistrict(value);
    setCities([]);
    setEditableData(prev => ({ ...prev, district: value, city: '' }));
    if (value) await fetchCities(value);
  };

  const handleReligionChange = (value) => {
    setEditableData(prev => ({ ...prev, religion: value, caste: '', subcaste: '' }));

    if (!value) {
      setFilteredCastes([]);
      return;
    }

    const casteMap = {
      'Hindu': Hindu,
      'Muslim': MuslimShia,
      'MuslimShia': MuslimShia,
      'MuslimSunni': MuslimSunni,
      'Christian': Christian,
      'Sikh': Sikh,
      'Jain': JainDigambar,
      'JainDigambar': JainDigambar,
      'JainShwetambar': JainShwetambar,
      'Buddhist': Buddhist,
      'Parsi': Parsi,
      'Jewish': Jewish,
      'InterReligion': InterReligion,
      'NoReligiousBelief': NoReligiousBelief,
    };

    const rawCastes = casteMap[value] || [];
    // ✅ extractCasteNames call karo - raw objects nahi
    setFilteredCastes(extractCasteNames(rawCastes));
  };

    const handleSave = async () => {
    setSaving(true);
    try {
      if (imageFile) {
        const uploaded = await uploadProfileImage();
        if (!uploaded) { setSaving(false); return; }
      }
      const updateData = {
        name: editableData.name || "",
        firstname: editableData.name || "",
        first_name: editableData.name || "",
        gender: editableData.gender || "",
        age: editableData.age ? parseInt(editableData.age) : null,
        dob: editableData.dob || "",
        height: editableData.height || "",
        weight: editableData.weight || "",
        nationality: editableData.nationality || "",
        religion: editableData.religion || "",
        caste: editableData.caste || "",
        subcaste: editableData.subcaste || "",
        mother_tongue: editableData.motherTongue || "",
        motherTongue: editableData.motherTongue || "",
        marrige_status: editableData.marrige_status || "",
        maritalstatus: editableData.marrige_status || "",
        blood_group: editableData.blood_group || "",
        location: editableData.city || editableData.location || "",
        smoking: editableData.smoking || "",
        drinking: editableData.drinking || "",
        foodLifestyle: editableData.foodLifestyle || editableData.Lifestyle || "",
        about: editableData.additional_notes || "",
        additional_notes: editableData.additional_notes || "",
        hobbies: editableData.hobbies || [],
        interests: editableData.interests || [],
        languages: editableData.languages || [],
        father_name: editableData.father_name || "",
        mother_name: editableData.mother_name || "",
        father_occupation: editableData.father_occupation || "",
        mother_occupation: editableData.mother_occupation || "",
        brother: parseInt(editableData.brother) || 0,
        sister: parseInt(editableData.sister) || 0,
        brother_marrige: parseInt(editableData.brother_marrige) || 0,
        sister_marrige: parseInt(editableData.sister_marrige) || 0,
        siblings: {
          brothers: parseInt(editableData.brother) || 0,
          sisters: parseInt(editableData.sister) || 0,
          brothers_married: parseInt(editableData.brother_marrige) || 0,
          sisters_married: parseInt(editableData.sister_marrige) || 0,
        },
        family_status: editableData.family_status || "",
        family_type: editableData.family_type || "",
        family_values: editableData.family_values || "",
        university: editableData.university || "",
        education: editableData.course || "",
        course: editableData.course || "",
        sub_course: editableData.sub_course || "",
        occupation: editableData.job_title || "",
        job_title: editableData.job_title || "",
        job_type: editableData.job_type || "",
        annual_income: editableData.annual_income ? parseInt(editableData.annual_income) : null,
        income: editableData.annual_income ? parseInt(editableData.annual_income) : null,
        Lifestyle: editableData.foodLifestyle || editableData.Lifestyle || "",
        created_profile: editableData.created_profile || "",
        height_preference: editableData.height_preference || "",
        age_preference: editableData.age_preference || "",
        education_preference: editableData.education_preference || "",
        religion_preference: editableData.religion_preference || "",
        location_preference: editableData.location_preference || "",
        partner_smoking: editableData.partner_smoking || "Any",
        partner_drinking: editableData.partner_drinking || "Any",
        partner_diet: editableData.partner_diet || "Any",
        email: editableData.email || "",
        contact: editableData.contact || "",
        contact1: editableData.contact1 || "",
        city: editableData.city || "",
        state: editableData.state || "",
        district: editableData.district || "",
        country: editableData.country || "India",
      };

      const cleanUserId = userId.replace(/\/$/, '');
      const res = await Api.put(
        `/api/user/profile/${cleanUserId}/`,
        updateData,
        { headers: { "Content-Type": "application/json" } }
      );

      if (res.data) {
        message.success(res.data.message || "✅ Profile updated!");
        if (updateData.gender) localStorage.setItem('user_gender', updateData.gender);
        
        // LocalStorage sync
        if (updateData.name) localStorage.setItem('username', updateData.name);

        setIsEditing(false);
        await fetchProfile();
        await fetchStats();

        // ✅ Header ko real-time update karne ke liye Event Dispatch
        window.dispatchEvent(new Event('profileUpdated'));
      }
    } catch (error) {
      const errData = error?.response?.data;
      if (errData && typeof errData === 'object') {
        const msgs = Object.entries(errData)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
          .join(' | ');
        message.error(msgs || "Failed to update profile");
      } else {
        message.error("Network error. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: "My Profile", url });
    else { navigator.clipboard.writeText(url); message.success("Link copied!"); }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      setEditableData({ ...profileData });
      setImagePreview(null);
      setImageFile(null);
      const el = document.getElementById('profileUpload');
      if (el) el.value = '';
    }
    setIsEditing(prev => !prev);
  };

  const updateField = (key, value) => {
    setEditableData(prev => {
      const updated = { ...prev, [key]: value };

      // ✅ DOB se age auto calculate
      if (key === 'dob' && value) {
        const calculatedAge = calculateAge(value);
        if (calculatedAge) updated.age = calculatedAge;
      }

      // ✅ Sibling validation
      if (['brother_marrige', 'sister_marrige'].includes(key)) {
        updated[key] = validateSiblingCount(key, value, prev);
      }

      // ✅ Total brothers kam ho jaye to married bhi adjust karo
      if (key === 'brother') {
        const total = parseInt(value) || 0;
        const married = parseInt(prev.brother_marrige) || 0;
        if (married > total) {
          updated.brother_marrige = total;
          message.info(`Married Brothers adjusted to ${total}`);
        }
      }

      if (key === 'sister') {
        const total = parseInt(value) || 0;
        const married = parseInt(prev.sister_marrige) || 0;
        if (married > total) {
          updated.sister_marrige = total;
          message.info(`Married Sisters adjusted to ${total}`);
        }
      }

      return updated;
    });
  };
  const statsConfig = [
    { label: "Profile Views", value: profileData?.profileViews || 0, icon: FaEye, gradient: "linear-gradient(135deg,#ff0055,#ff4d88)" },
    { label: "Shortlisted By", value: profileData?.shortlistedBy || 0, icon: FaStar, gradient: "linear-gradient(135deg,#ff9800,#ffb74d)" },
    { label: "Interests Received", value: profileData?.interestsReceived || 0, icon: FaHeart, gradient: "linear-gradient(135deg,#ff4d88,#ff80aa)" },

    {
      label: "Last Login",        // ← "Member Since" se change
      value: lastLogin,           // ← localStorage se
      icon: FaCalendar,
      gradient: "linear-gradient(135deg,#4caf50,#66bb6a)"
    },
  ];

  const tabsConfig = [
    { key: 'details', label: 'Personal', icon: FaUser },
    { key: 'family', label: 'Family', icon: FaUsers },
    { key: 'education', label: 'Education', icon: FaGraduationCap },
    { key: 'preferences', label: 'Preferences', icon: FaHeart },
    { key: 'contact', label: 'Contact', icon: FaPhone },
  ];

  const renderField = (item) => {
    const { key, type, options, editable = true } = item;

    if (!isEditing || !editable) {
      return <span>
        {key === 'city'
          ? (profileData.city || profileData.location || 'N/A')
          // ✅ Height ke liye CM to Feet convert karo
          : key === 'height'
            ? (profileData.height ? cmToFeetInches(profileData.height) : 'N/A')
            : (profileData[key] ?? 'N/A')
        }
      </span>
    }

    if (key === 'city') {
      return (
        <input
          type="text"
          value={editableData.city || ''}
          onChange={(e) => updateField('city', e.target.value)}
          placeholder="Enter your city..."
          style={{
            width: '100%', padding: '8px 10px',
            border: '1.5px solid #d1d5db', borderRadius: '8px',
            fontSize: '13px', fontFamily: 'inherit',
            outline: 'none', background: '#fff', color: '#1a0a10',
            transition: 'all .3s'
          }}
          onFocus={(e) => e.target.style.borderColor = '#4caf50'}
          onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
        />
      );
    }

    if (key === 'height') {
      // ✅ Current value ko feet format me convert karo for display
      const currentHeightDisplay = editableData.height
        ? cmToFeetInches(editableData.height)
        : '';

      return (
        <select
          value={currentHeightDisplay}
          onChange={(e) => {
            const selectedFeetStr = e.target.value;
            // ✅ Save ke liye CM value store karo
            const cmValue = feetInchesToCm(selectedFeetStr);
            updateField('height', cmValue); // Backend ke liye CM save karo
          }}
          style={{
            width: '100%', padding: '8px 10px',
            border: '1.5px solid #d1d5db', borderRadius: '8px',
            fontSize: '13px', fontFamily: 'inherit', outline: 'none',
            transition: 'all .3s', background: '#fff', color: '#1a0a10',
            cursor: 'pointer'
          }}
          onFocus={(e) => e.target.style.borderColor = '#4caf50'}
          onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
        >
          <option value="">Select Height...</option>
          {generateHeightOptions().map((h, i) => (
            <option key={i} value={h}>{h}</option>
          ))}
        </select>
      );
    }

    if (key === 'religion') {
      return (
        <div>
          <select
            value={editableData.religion || ''}
            onChange={(e) => handleReligionChange(e.target.value)}
            style={{
              width: '100%', padding: '8px 10px',
              border: '1.5px solid #d1d5db', borderRadius: '8px',
              fontSize: '13px', fontFamily: 'inherit', outline: 'none',
              transition: 'all .3s', background: '#fff', color: '#1a0a10',
              cursor: 'pointer'
            }}
            onFocus={(e) => e.target.style.borderColor = '#4caf50'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
            <option value="">Select Religion...</option>
            {religionOptions.map((r) => (
              <option key={r.id || r.value} value={r.value}>
                {r.emoji ? `${r.emoji} ${r.label}` : r.label}
              </option>
            ))}
          </select>
        </div>
      );
    }
    if (key === 'caste') {
      // ✅ Strings array already hai (extractCasteNames se)
      const casteOptions = filteredCastes.map(caste => ({
        value: caste,
        label: caste,
      }));

      return (
        <div style={{ position: 'relative' }}>
          {/* ✅ Native HTML - Registration jaisa searchable */}
          <input
            type="text"
            list="caste-datalist"
            value={editableData.caste || ''}
            onChange={(e) => updateField('caste', e.target.value)}
            placeholder={
              filteredCastes.length > 0
                ? `Search from ${filteredCastes.length} castes...`
                : editableData.religion
                  ? 'Type your caste...'
                  : '⚠️ Select Religion first'
            }
            style={{
              width: '100%',
              padding: '8px 10px',
              border: `1.5px solid ${filteredCastes.length > 0 ? '#4caf50' : '#d1d5db'}`,
              borderRadius: '8px',
              fontSize: '13px',
              fontFamily: 'inherit',
              outline: 'none',
              background: '#fff',
              color: '#1a0a10',
              transition: 'all .3s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#4caf50'}
            onBlur={(e) => e.target.style.borderColor = filteredCastes.length > 0 ? '#4caf50' : '#d1d5db'}
          />

          {/* ✅ Datalist with extracted string names */}
          <datalist id="caste-datalist">
            {filteredCastes.map((caste, i) => (
              <option key={i} value={caste} />
            ))}
          </datalist>

          {/* ✅ Status hints */}
          {!editableData.religion && isEditing && (
            <span style={{
              fontSize: '10px', color: '#ff9800',
              display: 'block', marginTop: 3, fontWeight: 600
            }}>
              ⚠️ Select Religion first to see caste options
            </span>
          )}

        </div>
      );
    }

    if (type === 'select') {
      const isDistrict = key === 'district';
      const isCity = key === 'city';
      const hasOptions = options && options.length > 0;
      const isDisabled = (isDistrict && !selectedState) || (isCity && !selectedDistrict);
      const placeholderText = isDistrict ? 'Select State first' : (isCity ? 'Select District first' : `Select ${item.label}...`);

      return (
        <select
          value={editableData[key] || ''}
          onChange={(e) => {
            const value = e.target.value;
            if (key === 'state') handleStateChange(value);
            else if (key === 'district') handleDistrictChange(value);
            else if (key === 'religion') handleReligionChange(value);
            else updateField(key, value);
          }}
          disabled={isDisabled}
          style={{
            width: '100%', padding: '8px 10px',
            border: '1.5px solid #d1d5db', borderRadius: '8px',
            fontSize: '13px', fontFamily: 'inherit', outline: 'none',
            transition: 'all .3s',
            background: isDisabled ? '#f5f5f5' : '#fff',
            color: isDisabled ? '#999' : '#1a0a10',
            cursor: isDisabled ? 'not-allowed' : 'pointer'
          }}
        >
          <option value="">{placeholderText}</option>
          {hasOptions && options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }

    if (type === 'textarea') {
      return (
        <textarea
          value={editableData[key] || ''}
          onChange={(e) => updateField(key, e.target.value)}
          rows={3}
          style={{
            width: '100%', padding: 8, borderRadius: 8,
            border: '1.5px solid #d1d5db', fontFamily: 'inherit',
            resize: 'vertical', outline: 'none', fontSize: '13px'
          }}
        />
      );
    }

    return (
      <input
        type={type || 'text'}
        value={editableData[key] ?? ''}
        onChange={(e) => updateField(key, e.target.value)}
        style={{
          width: '100%', padding: '8px 10px',
          border: '1.5px solid #d1d5db', borderRadius: '8px',
          fontSize: '13px', fontFamily: 'inherit', outline: 'none',
          transition: 'all .3s', background: '#fff', color: '#1a0a10'
        }}
        onFocus={(e) => e.target.style.borderColor = '#4caf50'}
        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
      />
    );
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        minHeight: '60vh', flexDirection: 'column', gap: 16, padding: '20px'
      }}>
        <FaSpinner size={40} style={{ color: '#e91e63', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#6b7280', fontSize: 13 }}>Loading Profile...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');
        * { font-family: 'Poppins', sans-serif !important; box-sizing: border-box; margin: 0; }
        @keyframes pfShimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* ===== PAGE ===== */
        .pf-page { background: #fff; min-height: 100vh; padding: 16px 12px; }
        .pf-wrap { max-width: 1280px; margin: 0 auto; }

        /* ===== BACK BTN ===== */
        .pf-back-btn {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 9px 18px;
          background: transparent;
          border: 2px solid #ffe4ec;
          border-radius: 12px;
          cursor: pointer;
          font-weight: 600;
          font-size: 13px;
          color: #555;
          transition: all .3s;
          margin-bottom: 16px;
        }
        .pf-back-btn:hover { border-color: #ff0055; color: #ff0055; background: #fff0f3; }

        /* ===== HEADER CARD ===== */
        .pf-header-card {
          background: #fff;
          border: 2px solid #ffe4ec;
          border-radius: 22px;
          padding: 24px;
          margin-bottom: 18px;
          box-shadow: 0 10px 30px rgba(255,0,85,.06);
          display: flex;
          gap: 24px;
          flex-wrap: wrap;
          position: relative;
          overflow: hidden;
        }
        .pf-header-card::before {
          content: "";
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 5px;
          background: linear-gradient(90deg,#ff0055,#ff4d88,#ff80aa,#ff0055);
          background-size: 200% 100%;
          animation: pfShimmer 3s linear infinite;
        }

        /* ===== AVATAR SECTION ===== */
        .pf-avatar-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          flex-shrink: 0;
        }
        .pf-avatar-wrap {
          position: relative;
          padding: 4px;
          background: linear-gradient(135deg,#ff0055,#ff4d88,#ff80aa);
          border-radius: 50%;
          box-shadow: 0 10px 26px rgba(255,0,85,.22);
        }
        .pf-avatar {
          width: 130px;
          height: 130px;
          border-radius: 50%;
          object-fit: cover;
          border: 4px solid #fff;
          display: block;
        }
        .pf-avatar.preview { opacity: .8; }
        .pf-online-dot {
          position: absolute;
          bottom: 8px; right: 10px;
          width: 20px; height: 20px;
          border-radius: 50%;
          background: #4caf50;
          border: 3px solid #fff;
        }
        .pf-camera-btn {
          position: absolute;
          bottom: 4px; right: -5px;
          width: 40px; height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg,#ff0055,#ff4d88);
          color: #fff;
          border: 3px solid #fff;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 5px 16px rgba(255,0,85,.3);
          transition: all .3s;
          z-index: 2;
        }
        .pf-camera-btn:hover { transform: scale(1.12) rotate(-10deg); }
        .pf-camera-btn:disabled { opacity: .5; cursor: not-allowed; transform: none !important; }
        .pf-remove-btn {
          position: absolute;
          top: -6px; right: -6px;
          width: 26px; height: 26px;
          border-radius: 50%;
          background: #f44336;
          color: #fff;
          border: 2px solid #fff;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          z-index: 3;
          font-size: 9px;
        }
        .pf-badge-row {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
          justify-content: center;
        }
        .pf-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 700;
        }
        .pf-badge.verified { background: linear-gradient(135deg,#e8f5e9,#c8e6c9); color: #2e7d32; border: 1px solid #a5d6a7; }
        .pf-badge.premium { background: linear-gradient(135deg,#fff3e0,#ffe0b2); color: #e65100; border: 1px solid #ffcc80; }

        /* ===== INFO SECTION ===== */
        .pf-info { flex: 1; min-width: 200px; }
        .pf-name {
          font-size: 24px;
          font-weight: 800;
          color: #1a0a10;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }
        .pf-name-gender { font-size: 13px; font-weight: 500; color: #888; }
        .pf-bid {
          font-size: 12px;
          color: #ff4d88;
          font-weight: 700;
          margin: 6px 0;
          padding: 3px 12px;
          border-radius: 999px;
          background: linear-gradient(135deg,#fff0f3,#ffe4ec);
          display: inline-block;
          border: 1px solid #ffd3df;
        }
        .pf-quick-info {
          font-size: 13px;
          color: #555;
          margin: 7px 0;
          font-weight: 500;
          line-height: 1.6;
        }
        .pf-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin: 10px 0;
        }
        .pf-tag {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          background: linear-gradient(135deg,#fff0f3,#ffe4ec);
          border: 1px solid #ffd3df;
          border-radius: 999px;
          font-size: 11px;
          color: #c2185b;
          font-weight: 600;
        }
        .pf-tag.online { background: linear-gradient(135deg,#e8f5e9,#c8e6c9); color: #4caf50; border-color: #a5d6a7; }

        /* ===== HEADER ACTIONS ===== */
        .pf-actions {
          display: flex;
          gap: 7px;
          margin-top: 12px;
          flex-wrap: wrap;
        }
        .pf-btn {
          padding: 9px 16px;
          border: none;
          border-radius: 11px;
          font-size: 12.5px;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          transition: all .25s;
          white-space: nowrap;
        }
        .pf-btn:hover { transform: translateY(-2px); }
        .pf-btn:disabled { opacity: .6; cursor: not-allowed; transform: none !important; }
        .pf-btn-edit { background: linear-gradient(135deg,#ff0055,#ff4d88); color: #fff; box-shadow: 0 5px 16px rgba(255,0,85,.22); }
        .pf-btn-save { background: linear-gradient(135deg,#4caf50,#66bb6a); color: #fff; }
        .pf-btn-cancel { background: #f5f5f7; color: #888; }
        .pf-btn-share { background: #fff; color: #ff0055; border: 1.5px solid #ff0055 !important; }
        .pf-btn-share:hover { background: #fff0f3; }

        /* ===== STATS GRID ===== */
        .pf-stats-grid {
          display: grid;
          grid-template-columns: repeat(4,1fr);
          gap: 12px;
          margin-bottom: 18px;
        }
        .pf-stat-card {
          background: #fff;
          border: 2px solid #ffe4ec;
          border-radius: 15px;
          padding: 14px;
          display: flex;
          align-items: center;
          gap: 10px;
          transition: all .3s;
        }
        .pf-stat-card:hover { transform: translateY(-3px); border-color: #ffb3c8; }
        .pf-stat-icon {
          width: 44px; height: 44px;
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px;
          color: #fff;
          flex-shrink: 0;
        }
        .pf-stat-value { font-size: 18px; font-weight: 800; color: #1a0a10; margin: 0; }
        .pf-stat-label { font-size: 11px; color: #888; margin: 2px 0 0; }

        /* ===== TABS ===== */
        .pf-tabs {
          display: flex;
          gap: 3px;
          padding: 4px;
          background: #fff0f3;
          border: 2px solid #ffe4ec;
          border-radius: 13px;
          margin-bottom: 16px;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }
        .pf-tabs::-webkit-scrollbar { display: none; }
        .pf-tab {
          padding: 9px 16px;
          border: none;
          border-radius: 9px;
          cursor: pointer;
          font-size: 12.5px;
          font-weight: 700;
          transition: all .25s;
          background: transparent;
          color: #888;
          display: flex;
          align-items: center;
          gap: 5px;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .pf-tab:hover { color: #ff0055; }
        .pf-tab.active { background: linear-gradient(135deg,#ff0055,#ff4d88); color: #fff; }

        /* ===== CONTENT ===== */
        .pf-content {
          background: #fff;
          border: 2px solid #ffe4ec;
          border-radius: 18px;
          padding: 22px;
          margin-bottom: 18px;
          transition: border-color .3s;
        }
        .pf-content.editing { border-color: #4caf50; box-shadow: 0 0 0 3px rgba(76,175,80,.08); }
        .pf-section-title {
          font-size: 17px;
          font-weight: 800;
          color: #1a0a10;
          margin: 0 0 16px;
          display: flex;
          align-items: center;
          gap: 8px;
          padding-bottom: 12px;
          border-bottom: 2px solid #fff0f3;
          flex-wrap: wrap;
        }
        .pf-edit-badge {
          font-size: 11px;
          background: #4caf50;
          color: #fff;
          padding: 2px 10px;
          border-radius: 999px;
          font-weight: 600;
        }

        /* ===== FIELDS GRID ===== */
        .pf-grid {
          display: grid;
          grid-template-columns: repeat(2,1fr);
          gap: 12px;
        }
        .pf-field {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 12px;
          background: linear-gradient(135deg,#fffafc,#fff0f3);
          border: 1.5px solid #ffe4ec;
          border-radius: 11px;
          transition: all .25s;
        }
        .pf-field:hover { border-color: #ffb3c8; }
        .pf-field.editing { border-color: #4caf50; background: #f0fff4; }
        .pf-label {
          font-size: 10.5px;
          color: #c2185b;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: .5px;
          margin: 0;
        }
        .pf-value {
          font-size: 13.5px;
          color: #1a0a10;
          font-weight: 600;
          margin: 0;
        }

        /* ===== ABOUT ===== */
        .pf-about {
          margin-top: 18px;
          padding: 16px 18px;
          background: linear-gradient(135deg,#fff0f3,#ffe4ec);
          border: 1.5px solid #ffd3df;
          border-radius: 13px;
        }
        .pf-about.editing { border-color: #4caf50; background: #f0fff4; }
        .pf-about-title { font-size: 15px; font-weight: 800; color: #c2185b; margin: 0 0 8px; }
        .pf-about-text { font-size: 13px; color: #555; line-height: 1.7; margin: 0; }

        /* ===== HOBBIES ===== */
        .pf-hobbies { margin-top: 18px; }
        .pf-hobbies-title { font-size: 15px; font-weight: 800; color: #1a0a10; margin: 0 0 10px; }
        .pf-tags-list { display: flex; flex-wrap: wrap; gap: 7px; }
        .pf-hobby-tag {
          padding: 6px 14px;
          background: linear-gradient(135deg,#fff0f3,#ffe4ec);
          border: 1.5px solid #ffd3df;
          border-radius: 999px;
          font-size: 12px;
          color: #c2185b;
          font-weight: 600;
          cursor: default;
          transition: all .25s;
        }
        .pf-hobby-tag:hover {
          background: linear-gradient(135deg,#ff0055,#ff4d88);
          color: #fff; border-color: #ff0055;
          transform: translateY(-2px);
        }
        .pf-hobby-tag.interest {
          background: linear-gradient(135deg,#e8f5e9,#c8e6c9);
          border-color: #a5d6a7; color: #2e7d32;
        }
        .pf-hobby-input {
          padding: 7px 12px;
          border: 1.5px solid #d1d5db;
          border-radius: 8px;
          font-size: 12.5px;
          outline: none;
          width: 100%;
          font-family: inherit;
        }
        .pf-hobby-input:focus { border-color: #4caf50; }

        /* ===== IMG STATUS ===== */
        .pf-img-status { font-size: 11px; color: #4caf50; font-weight: 600; margin-top: 3px; }

        /* ===== BOTTOM ACTIONS ===== */
        .pf-bottom {
          text-align: center;
          margin-top: 20px;
          display: flex;
          gap: 10px;
          justify-content: center;
          flex-wrap: wrap;
        }
        .pf-bottom-btn {
          padding: 12px 30px;
          border: none;
          border-radius: 999px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all .3s;
        }
        .pf-bottom-btn:hover { transform: translateY(-2px); }
        .pf-bottom-btn:disabled { opacity: .6; cursor: not-allowed; transform: none !important; }
        .pf-bottom-edit {
          background: linear-gradient(135deg,#ff0055,#ff4d88,#ff80aa);
          color: #fff;
          box-shadow: 0 8px 24px rgba(255,0,85,.24);
        }
        .pf-bottom-save { background: linear-gradient(135deg,#4caf50,#66bb6a); color: #fff; }
        .pf-bottom-cancel { background: #f5f5f7; color: #888; }

        /* =======================================================
           TABLET - 769px to 1024px
           ======================================================= */
        @media (max-width: 1024px) {
          .pf-stats-grid { grid-template-columns: repeat(2,1fr); }
        }

        /* =======================================================
           MOBILE - max 768px
           ======================================================= */
        @media (max-width: 768px) {
          .pf-page { padding: 12px 10px; }

          /* Header card - center on mobile */
          .pf-header-card {
            flex-direction: column;
            align-items: center;
            text-align: center;
            padding: 20px 16px;
            gap: 16px;
          }
          .pf-info { min-width: unset; width: 100%; }
          .pf-name {
            justify-content: center;
            font-size: 20px;
          }
          .pf-quick-info { font-size: 12px; }
          .pf-tags { justify-content: center; }
          .pf-actions { justify-content: center; }

          /* Stats - 2 col */
          .pf-stats-grid {
            grid-template-columns: repeat(2,1fr);
            gap: 10px;
          }
          .pf-stat-card { padding: 12px; gap: 8px; }
          .pf-stat-icon { width: 38px; height: 38px; border-radius: 10px; font-size: 16px; }
          .pf-stat-value { font-size: 16px; }
          .pf-stat-label { font-size: 10px; }

          /* Content */
          .pf-content { padding: 16px; border-radius: 16px; }
          .pf-section-title { font-size: 15px; }

          /* Grid - single col on mobile */
          .pf-grid { grid-template-columns: 1fr; gap: 10px; }
          .pf-field { padding: 10px; }

          /* Tabs */
          .pf-tab { padding: 8px 12px; font-size: 11.5px; }

          /* Bottom */
          .pf-bottom-btn { padding: 11px 24px; font-size: 13px; }
        }

        /* =======================================================
           MOBILE SMALL - max 480px
           ======================================================= */
        @media (max-width: 480px) {
          .pf-page { padding: 10px 8px; }

          /* Avatar smaller */
          .pf-avatar { width: 110px; height: 110px; }
          .pf-camera-btn { width: 36px; height: 36px; }

          /* Name */
          .pf-name { font-size: 18px; }
          .pf-quick-info { font-size: 11.5px; }

          /* Back button */
          .pf-back-btn { padding: 8px 14px; font-size: 12px; }

          /* Stat card - smaller */
          .pf-stat-card { padding: 10px; gap: 7px; }
          .pf-stat-icon { width: 34px; height: 34px; border-radius: 9px; font-size: 14px; }
          .pf-stat-value { font-size: 15px; }
          .pf-stat-label { font-size: 9.5px; }

          /* Header card */
          .pf-header-card { padding: 16px 14px; }

          /* Tabs - icon only hint */
          .pf-tab { padding: 7px 10px; font-size: 11px; }

          /* Content */
          .pf-content { padding: 14px 12px; }
          .pf-section-title { font-size: 14px; gap: 6px; }

          /* Field */
          .pf-field { padding: 10px; }
          .pf-label { font-size: 9.5px; }
          .pf-value { font-size: 12.5px; }

          /* Hobby inputs full width */
          .pf-hobby-input { width: 100%; font-size: 12px; }

          /* Buttons */
          .pf-btn { padding: 8px 14px; font-size: 12px; }
          .pf-bottom-btn { padding: 10px 20px; font-size: 12px; }
          .pf-bottom { gap: 8px; }

          /* About */
          .pf-about { padding: 14px; }
          .pf-about-title { font-size: 13.5px; }
          .pf-about-text { font-size: 12.5px; }

          /* Badge */
          .pf-bid { font-size: 11px; padding: 3px 10px; }
        }

        /* =======================================================
           MOBILE EXTRA SMALL - max 360px
           ======================================================= */
        @media (max-width: 360px) {
          .pf-avatar { width: 95px; height: 95px; }
          .pf-name { font-size: 16px; }

          /* Stats - 2 col (keep but smaller) */
          .pf-stat-card { padding: 8px; }
          .pf-stat-icon { width: 30px; height: 30px; font-size: 12px; }
          .pf-stat-value { font-size: 13px; }
          .pf-stat-label { font-size: 9px; }

          /* Tabs */
          .pf-tab { padding: 6px 9px; font-size: 10.5px; gap: 3px; }
          .pf-tab svg { display: none; }

          /* Content */
          .pf-content { padding: 12px 10px; }

          /* Buttons full width */
          .pf-actions { flex-direction: column; align-items: stretch; }
          .pf-btn { justify-content: center; }
          .pf-bottom { flex-direction: column; align-items: center; }
          .pf-bottom-btn { width: 100%; justify-content: center; }
        }
          /* Existing style tag me add karo */
@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}
      `}</style>

      <div className="pf-page">
        <div className="pf-wrap">

          {/* Back */}
          <button className="pf-back-btn" onClick={() => navigate('/dashboard')}>
            <FaArrowLeft size={14} /> Back to Dashboard
          </button>

          {/* ===== HEADER CARD ===== */}
          <div className="pf-header-card">

            {/* Avatar */}
            <div className="pf-avatar-section">
              <div className="pf-avatar-wrap">
                <img
                  src={imagePreview || profileData?.image ||
                    `https://ui-avatars.com/api/?name=${encodeURIComponent(profileData?.name || 'U')}&background=ff0055&color=fff&size=200`}
                  alt="Profile"
                  className={`pf-avatar ${imagePreview ? 'preview' : ''}`}
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(profileData?.name || 'U')}&background=ff0055&color=fff&size=200`;
                  }}
                />


                {isEditing && imagePreview && (
                  <button className="pf-remove-btn" onClick={() => {
                    setImagePreview(null);
                    setImageFile(null);
                    const el = document.getElementById('profileUpload');
                    if (el) el.value = '';
                  }} title="Remove photo">
                    <FaTrash />
                  </button>
                )}

                <input
                  type="file"
                  id="profileUpload"
                  hidden
                  accept="image/*"
                  onChange={handleImageChange}
                  disabled={!isEditing || imageUploading}
                />

                <button
                  className="pf-camera-btn"
                  onClick={() => document.getElementById("profileUpload")?.click()}
                  disabled={!isEditing || imageUploading || saving}
                  title={isEditing ? "Change photo" : "Enable edit mode first"}
                >
                  {imageUploading
                    ? <FaSpinner size={13} style={{ animation: 'spin 1s linear infinite' }} />
                    : <FaCamera size={13} />}
                </button>
              </div>

              {isEditing && imagePreview && (
                <span className="pf-img-status">📸 Click Save to upload</span>
              )}

              <div className="pf-badge-row">
                {profileData?.verified && (
                  <span className="pf-badge verified">
                    <FaCheckCircle size={10} /> Verified
                  </span>
                )}
                {profileData?.premium && (
                  <span className="pf-badge premium">
                    <FaStar size={10} /> Premium
                  </span>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="pf-info">
              <h1 className="pf-name">
                {profileData?.name || "Your Name"}
                <span className="pf-name-gender">({profileData?.gender || "N/A"})</span>
              </h1>
              <div className="pf-bid">
                Profile ID: {profileData?.profile_id || 'N/A'}
              </div>
              <p className="pf-quick-info">
                🎂 {profileData?.age || 'N/A'} Yrs &nbsp;•&nbsp;
                {/* ✅ CM to Feet convert karke dikhao */}
                📏 {profileData?.height ? cmToFeetInches(profileData.height) : 'N/A'} &nbsp;•&nbsp;
                📍 {profileData?.city || profileData?.location || 'N/A'}
              </p>
              <div className="pf-tags">
                {/* Online Status Tag - Dynamic */}
                <span className={`pf-tag ${profileData?.online ? 'online' : ''}`}>
                  <span style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: profileData?.online ? '#4caf50' : '#bbb',
                    display: 'inline-block',
                    // ✅ Blinking animation for online
                    animation: profileData?.online ? 'blink 1.5s infinite' : 'none',
                  }} />
                  {profileData?.online ? '🟢 Online' : '⚫ Offline'}
                </span>


                
                <span className="pf-tag" style={{
                  background: 'linear-gradient(135deg,#e8f5e9,#c8e6c9)',
                  color: '#2e7d32', borderColor: '#a5d6a7'
                }}>
                  <FaCheckCircle size={9} /> Active
                </span>
              </div>

              <div className="pf-actions">
                {!isEditing ? (
                  <button className="pf-btn pf-btn-edit" onClick={() => setIsEditing(true)}>
                    <FaEdit size={11} /> Edit Profile
                  </button>
                ) : (
                  <>
                    <button
                      className="pf-btn pf-btn-save"
                      onClick={handleSave}
                      disabled={saving || imageUploading}
                    >
                      {(saving || imageUploading)
                        ? <FaSpinner size={11} style={{ animation: 'spin 1s linear infinite' }} />
                        : <FaSave size={11} />}
                      {(saving || imageUploading) ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className="pf-btn pf-btn-cancel"
                      onClick={handleEditToggle}
                      disabled={saving || imageUploading}
                    >
                      <FaTimes size={11} /> Cancel
                    </button>
                  </>
                )}
                <button className="pf-btn pf-btn-share" onClick={handleShare}>
                  <FaShare size={11} /> Share
                </button>
              </div>
            </div>
          </div>

          {/* ===== STATS ===== */}
          <div className="pf-stats-grid">
            {statsConfig.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div key={i} className="pf-stat-card">
                  <div className="pf-stat-icon" style={{ background: stat.gradient }}>
                    <Icon />
                  </div>
                  <div>
                    <p className="pf-stat-value">{stat.value}</p>
                    <p className="pf-stat-label">{stat.label}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ===== TABS ===== */}
          <div className="pf-tabs">
            {tabsConfig.map(tab => (
              <button
                key={tab.key}
                className={`pf-tab ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                <tab.icon size={11} /> {tab.label}
              </button>
            ))}
          </div>

          {/* ===== CONTENT ===== */}
          <div className={`pf-content ${isEditing ? 'editing' : ''}`}>

            {/* ---- PERSONAL TAB ---- */}
            {activeTab === 'details' && (
              <>
                <h3 className="pf-section-title">
                  <FaUser size={15} /> Personal Details
                  {isEditing && <span className="pf-edit-badge">✏️ Editing</span>}
                </h3>
                <div className="pf-grid">
                  {[
                    { label: 'Full Name', key: 'name' },
                    { label: 'Profile ID', key: 'profile_id', editable: false },
                    { label: 'Gender', key: 'gender', type: 'select', options: ['Male', 'Female', 'Other'] },
                    { label: 'Age (Auto)', key: 'age', type: 'number', editable: false },
                    { label: 'Date of Birth', key: 'dob', type: 'date' },
                    {
                      label: 'Height', key: 'height', type: 'select',
                      options: generateHeightOptions()
                    },
                    { label: 'Religion', key: 'religion' },
                    {
                      label: 'Caste',
                      key: 'caste',
                      type: filteredCastes.length > 0 ? 'select' : 'text',
                      options: filteredCastes.length > 0 ? filteredCastes : []
                    },
                    { label: 'Sub Caste', key: 'subcaste' },
                    { label: 'Mother Tongue', key: 'motherTongue', type: 'select', options: ['Hindi', 'English', 'Marathi', 'Punjabi', 'Gujarati', 'Bengali', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Odia', 'Assamese', 'Urdu'] },
                    { label: 'Marital Status', key: 'marrige_status', type: 'select', options: ['Unmarried', 'Divorced', 'Widowed'] },
                    { label: 'Blood Group', key: 'blood_group', type: 'select', options: ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'] },
                    { label: 'Smoking', key: 'smoking', type: 'select', options: ['Yes', 'No', 'Occasionally'] },
                    { label: 'Drinking', key: 'drinking', type: 'select', options: ['Yes', 'No', 'Occasionally'] },
                    { label: 'Food Lifestyle', key: 'foodLifestyle', type: 'select', options: ['Vegetarian', 'Non-Vegetarian', 'Eggetarian'] },
                  ].map((item, i) => (
                    <div key={i} className={`pf-field ${isEditing && item.editable !== false ? 'editing' : ''}`}>
                      <p className="pf-label">{item.label}</p>
                      <div className="pf-value">{renderField(item)}</div>
                    </div>
                  ))}
                </div>

                {/* About */}
                <div className={`pf-about ${isEditing ? 'editing' : ''}`} style={{ marginTop: 14 }}>
                  <h4 className="pf-about-title"><FaNotesMedical /> About Me</h4>
                  {isEditing ? (
                    <textarea
                      value={editableData.additional_notes || ''}
                      onChange={(e) => updateField('additional_notes', e.target.value)}
                      placeholder="Write something about yourself..."
                      rows={3}
                      style={{
                        width: '100%', padding: 10,
                        border: '1.5px solid #d1d5db',
                        borderRadius: 8, fontFamily: 'inherit',
                        resize: 'vertical', outline: 'none', fontSize: 13
                      }}
                    />
                  ) : (
                    <p className="pf-about-text">{profileData.additional_notes || 'No notes added.'}</p>
                  )}
                </div>

                {/* Hobbies */}
                <div className="pf-hobbies">
                  <h4 className="pf-hobbies-title"><FaStar size={13} /> Hobbies & Interests</h4>
                  <div className="pf-tags-list">
                    {isEditing ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                        <input
                          className="pf-hobby-input"
                          type="text"
                          value={editableData.hobbies?.join(', ') || ''}
                          onChange={(e) => updateField('hobbies', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                          placeholder="Hobbies (comma separated)"
                        />
                        <input
                          className="pf-hobby-input"
                          type="text"
                          value={editableData.interests?.join(', ') || ''}
                          onChange={(e) => updateField('interests', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                          placeholder="Interests (comma separated)"
                        />
                      </div>
                    ) : (
                      <>
                        {profileData?.hobbies?.map((h, i) => <span key={i} className="pf-hobby-tag">{h}</span>)}
                        {profileData?.interests?.map((int, i) => <span key={i} className="pf-hobby-tag interest">{int}</span>)}
                        {!profileData?.hobbies?.length && !profileData?.interests?.length && (
                          <span style={{ color: '#999', fontSize: 12.5 }}>No hobbies added.</span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* ---- FAMILY TAB ---- */}
            {activeTab === 'family' && (
              <>
                <h3 className="pf-section-title">
                  <FaUsers size={15} /> Family Background
                  {isEditing && <span className="pf-edit-badge">✏️ Editing</span>}
                </h3>
                <div className="pf-grid">
                  {[
                    { label: "Father's Name", key: 'father_name' },
                    { label: "Father's Occupation", key: 'father_occupation', type: 'select', options: ['Government Employee', 'Private Employee', 'Business', 'Self-Employed', 'Farmer', 'Teacher', 'Doctor', 'Engineer', 'Lawyer', 'Architect', 'Banker', 'CA', 'Professor', 'Retired', 'Other'] },
                    { label: "Mother's Name", key: 'mother_name' },
                    { label: "Mother's Occupation", key: 'mother_occupation', type: 'select', options: ['Government Employee', 'Private Employee', 'Business', 'Self-Employed', 'Farmer', 'Teacher', 'Doctor', 'Engineer', 'Lawyer', 'Architect', 'Banker', 'CA', 'Professor', 'Retired', 'Housewife', 'Other'] },
                    { label: 'Total Brothers', key: 'brother', type: 'number' },
                    { label: 'Married Brothers', key: 'brother_marrige', type: 'number' },
                    { label: 'Total Sisters', key: 'sister', type: 'number' },
                    { label: 'Married Sisters', key: 'sister_marrige', type: 'number' },
                    { label: 'Family Status', key: 'family_status', type: 'select', options: ['Middle Class', 'Upper Middle Class', 'Rich', 'Affluent'] },
                    { label: 'Family Type', key: 'family_type', type: 'select', options: ['Nuclear', 'Joint'] },
                    { label: 'Family Values', key: 'family_values', type: 'select', options: ['Traditional', 'Moderate', 'Liberal'] },
                  ].map((item, i) => (
                    <div key={i} className={`pf-field ${isEditing ? 'editing' : ''}`}>
                      <p className="pf-label">{item.label}</p>
                      <div className="pf-value">{renderField(item)}</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* ---- EDUCATION TAB ---- */}
            {activeTab === 'education' && (
              <>
                <h3 className="pf-section-title">
                  <FaGraduationCap size={15} /> Education & Career
                  {isEditing && <span className="pf-edit-badge">✏️ Editing</span>}
                </h3>
                <div className="pf-grid">
                  {[
                    { label: 'University / College', key: 'university' },
                    { label: 'Highest Course', key: 'course', type: 'select', options: ['10th', '12th', 'Diploma', 'ITI', 'B.Sc', 'M.Sc', 'Ph.D', 'B.Tech', 'B.E.', 'M.Tech', 'M.E.', 'MBBS', 'MD/MS', 'BDS', 'MDS', 'BAMS', 'BHMS', 'BUMS', 'BPT', 'MPT', 'B.Pharm', 'M.Pharm', 'Nursing (B.Sc/M.Sc)', 'B.Com', 'M.Com', 'CA', 'CS', 'CMA', 'BBA', 'MBA', 'PGDM', 'BCA', 'MCA', 'BA', 'MA', 'B.Ed', 'M.Ed', 'D.Ed / D.El.Ed', 'LLB', 'LLM', 'BA LLB', 'Design', 'Hotel Management', 'Mass Communication / Journalism', 'B.Sc Agriculture', 'M.Sc Agriculture', 'BFA / MFA (Fine Arts)', 'Music / Dance', 'Aviation', 'Merchant Navy', 'Defence', 'Other'] },
                    { label: 'Specialization', key: 'sub_course' },
                    { label: 'Job Title', key: 'job_title' },
                    { label: 'Job Type', key: 'job_type', type: 'select', options: ['Govt', 'Private', 'Self Employed', 'Not Working'] },
                    { label: 'Annual Income (₹)', key: 'annual_income', type: 'number' },
                    { label: 'Lifestyle', key: 'Lifestyle' },
                    { label: 'Profile Managed By', key: 'created_profile', type: 'select', options: ['Self', 'Parent', 'Sibling', 'Friend', 'Staff', 'Other'] },
                  ].map((item, i) => (
                    <div key={i} className={`pf-field ${isEditing ? 'editing' : ''}`}>
                      <p className="pf-label">{item.label}</p>
                      <div className="pf-value">{renderField(item)}</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* ---- PREFERENCES TAB ---- */}
            {activeTab === 'preferences' && (
              <>
                <h3 className="pf-section-title">
                  <FaHeart size={15} /> Partner Preferences
                  {isEditing && <span className="pf-edit-badge">✏️ Editing</span>}
                </h3>
                <div className="pf-grid">
                  {[
                    { label: 'Height Preference', key: 'height_preference' },
                    { label: 'Age Preference', key: 'age_preference' },
                    { label: 'Education Preference', key: 'education_preference' },
                    { label: 'Religion Preference', key: 'religion_preference' },
                    { label: 'Location Preference', key: 'location_preference' },
                    { label: 'Partner Smoking', key: 'partner_smoking', type: 'select', options: ['Any', 'Yes', 'No', 'Occasionally'] },
                    { label: 'Partner Drinking', key: 'partner_drinking', type: 'select', options: ['Any', 'Yes', 'No', 'Occasionally'] },
                    { label: 'Partner Diet', key: 'partner_diet', type: 'select', options: ['Any', 'Vegetarian', 'Non-Vegetarian', 'Eggetarian'] },
                  ].map((item, i) => (
                    <div key={i} className={`pf-field ${isEditing ? 'editing' : ''}`}>
                      <p className="pf-label">{item.label}</p>
                      <div className="pf-value">{renderField(item)}</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* ---- CONTACT TAB ---- */}
            {activeTab === 'contact' && (
              <>
                <h3 className="pf-section-title">
                  <FaPhone size={15} /> Contact & Location
                  {isEditing && <span className="pf-edit-badge">✏️ Editing</span>}
                </h3>
                <div className="pf-grid">
                  {[
                    { label: 'Email', key: 'email', type: 'email' },
                    { label: 'Primary Mobile', key: 'contact' },
                    { label: 'Alternate Mobile', key: 'contact1' },
                    { label: 'Country', key: 'country' },
                    { label: 'State', key: 'state', type: 'select', options: states.map(s => s.state || s) },
                    { label: 'District', key: 'district', type: 'select', options: districts },
                    { label: 'City', key: 'city', type: 'text' },
                  ].map((item, i) => (
                    <div key={i} className={`pf-field ${isEditing ? 'editing' : ''}`}>
                      <p className="pf-label">{item.label}</p>
                      <div className="pf-value">{renderField(item)}</div>
                    </div>
                  ))}
                </div>
              </>
            )}

          </div>

          {/* ===== BOTTOM ACTIONS ===== */}
          <div className="pf-bottom">
            {!isEditing ? (
              <button className="pf-bottom-btn pf-bottom-edit" onClick={() => setIsEditing(true)}>
                <FaEdit size={13} /> Edit Profile Now
              </button>
            ) : (
              <>
                <button
                  className="pf-bottom-btn pf-bottom-save"
                  onClick={handleSave}
                  disabled={saving || imageUploading}
                >
                  {(saving || imageUploading)
                    ? <FaSpinner size={13} style={{ animation: 'spin 1s linear infinite' }} />
                    : <FaSave size={13} />}
                  {(saving || imageUploading) ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  className="pf-bottom-btn pf-bottom-cancel"
                  onClick={handleEditToggle}
                  disabled={saving || imageUploading}
                >
                  <FaTimes size={13} /> Cancel
                </button>
              </>
            )}
          </div>

        </div>
      </div>
    </>
  );
};

export default Profile;